// ========================================
// Settings View
// Global configuration (Holidays, etc.)
// ========================================

const SettingsView = {
    currentYear: new Date().getFullYear() - (new Date().getMonth() < 8 ? 1 : 0),
    _ready: false,
    _saving: false,
    _request: 0,
    canManage() { return AuthManager.canManageModule('settings'); },

    // Tabs: the school's data (one form across four tabs, so switching never loses what was
    // typed) and the school calendar.
    TABS: [
        {id: 'centro', label: 'Centro', icon: 'fa-school'},
        {id: 'contacto', label: 'Contacto', icon: 'fa-address-card'},
        {id: 'imagen', label: 'Imagen', icon: 'fa-image'},
        {id: 'privacidad', label: 'Privacidad', icon: 'fa-shield-halved'},
        {id: 'normativa', label: 'Normativa', icon: 'fa-scale-balanced'},
        {id: 'calendario', label: 'Calendario escolar', icon: 'fa-calendar-check'},
        {id: 'actualizacion', label: 'Actualización', icon: 'fa-clock-rotate-left', adminOnly: true}
    ],
    // Design A (Oct 2026): the sections in a grouped menu on the left.
    GROUPS: [
        {label: 'El centro', tabs: ['centro', 'contacto', 'imagen']},
        {label: 'Textos legales', tabs: ['privacidad', 'normativa']},
        {label: 'Calendario', tabs: ['calendario']},
        {label: 'Sistema', tabs: ['actualizacion']}
    ],
    MENU_LABELS: {centro: 'Datos del centro', imagen: 'Imagen y logos', actualizacion: 'Aviso de actualización'},
    currentTab: 'centro',
    // Tabs with their own buttons: the school data «Guardar» bar is hidden there.
    OWN_SAVE_TABS: ['calendario', 'actualizacion'],

    tabs() { return this.TABS.filter(t => !t.adminOnly || AuthManager.isAdmin()); },

    render() {
        if (!this.canManage() && !AuthManager.hasModule('settings')) return '<p role="alert">No tienes permisos para consultar la configuración global.</p>';
        const globalSettings = this.getGlobalSettings();
        const holidays = globalSettings.holidays || [];
        if (!this.tabs().some(t => t.id === this.currentTab)) this.currentTab = 'centro';
        const tab = this.currentTab;

        const visible = new Set(this.tabs().map(t => t.id));
        return `
            <div class="dnr-proposal settings-container sc-page sca">
                <nav class="sca-nav" aria-label="Secciones de ajustes">
                    <div class="dnr-hero-eyebrow sc-eyebrow">Ajustes</div>
                    <h1 class="sc-title">Configuración global</h1>
                    <div class="sca-menu" role="tablist" aria-orientation="vertical" aria-label="Secciones de ajustes">
                        ${this.GROUPS.map(g => g.tabs.filter(id => visible.has(id))).map((ids, i) => ids.length ? `
                            <span class="sca-group">${this.GROUPS[i].label}</span>
                            ${ids.map(id => { const t = this.TABS.find(x => x.id === id); return `
                            <button type="button" role="tab" id="sc-tab-${t.id}" aria-controls="sc-panel-${t.id}" aria-selected="${t.id === tab}" class="sc-tab sca-item ${t.id === tab ? 'is-active' : ''}"
                                ${UIEvents.attr("click", function(event,captured){SettingsView.switchTab(String(captured[0]));}, [t.id])}>
                                <i class="fas ${t.icon}" aria-hidden="true"></i><span class="sca-item-label">${this.MENU_LABELS[t.id] || t.label}</span><span class="sca-mark" id="sca-mark-${t.id}"></span>
                            </button>`; }).join('')}` : '').join('')}
                    </div>
                </nav>
                <div class="sca-main">
                ${this.renderSchoolProfile(tab)}

                <section class="card sc-panel" role="tabpanel" id="sc-panel-calendario" aria-labelledby="sc-tab-calendario" ${tab === 'calendario' ? '' : 'hidden'}>
                    <div class="sca-cal-head">
                        <h2 class="sc-panel-title">Calendario escolar</h2>
                        <div class="sca-year">
                            <button type="button" class="sca-btn" aria-label="Curso anterior" ${UIEvents.attr("click", function(event,captured){SettingsView.changeYear(-1);}, [])}><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
                            <b>Curso ${this.currentYear} / ${this.currentYear + 1}</b>
                            <button type="button" class="sca-btn" aria-label="Curso siguiente" ${UIEvents.attr("click", function(event,captured){SettingsView.changeYear(1);}, [])}><i class="fas fa-chevron-right" aria-hidden="true"></i></button>
                        </div>
                        <span class="sca-pill" id="sca-holiday-count"></span>
                    </div>
                    <p class="sc-help">Pulsa un día para marcarlo o quitarlo como festivo; se guarda al momento y sale en rojo en todas las formaciones.</p>
                    <div class="sca-legend" aria-hidden="true"><span><i style="background:#E53935"></i>Festivo entre semana</span><span><i style="background:#FBE4E1"></i>Sábado o domingo marcado como festivo</span><span><i style="border:1px solid #DBE3DF"></i>Día lectivo</span></div>
                    <p id="holidays-status" role="status">${this._ready ? '' : 'Cargando calendario…'}</p>
                    <div class="calendar-year-grid" aria-busy="${!this._ready || this._saving}">
                        ${this.renderMonths(this.currentYear, holidays)}
                    </div>
                </section>

                ${AuthManager.isAdmin() ? this.renderUpdateNotice(tab) : ''}
                </div>
            </div>
        `;
    },

    // Shows a tab without re-rendering, so edits in progress are kept.
    switchTab(id) {
        if (!this.tabs().some(t => t.id === id)) return;
        this.currentTab = id;
        this.tabs().forEach(t => {
            const button = document.getElementById(`sc-tab-${t.id}`);
            const panel = document.getElementById(`sc-panel-${t.id}`);
            if (button) { button.classList.toggle('is-active', t.id === id); button.setAttribute('aria-selected', String(t.id === id)); }
            if (panel) panel.hidden = t.id !== id;
        });
        const bar = document.getElementById('sc-save-bar');
        if (bar) bar.hidden = this.OWN_SAVE_TABS.includes(id);
    },

    // --- Aviso de actualización (server/runtime/update-notice.js): everybody sees it once ---
    renderUpdateNotice(tab) {
        return `
            <section class="card sc-panel" role="tabpanel" id="sc-panel-actualizacion" aria-labelledby="sc-tab-actualizacion" ${tab === 'actualizacion' ? '' : 'hidden'}>
                <h2 class="sc-panel-title">Aviso de actualización</h2>
                <p class="sc-help">Programa la próxima actualización de SICAF. Cada persona (personal, profesorado y alumnado) verá el aviso una sola vez: al entrar o, si ya está dentro, en unos segundos. Deja de mostrarse cuando pasa la hora. Publica siempre de noche.</p>
                <div class="sc-notice-status" id="sc-notice-status" role="status">Cargando aviso…</div>
                <form id="sc-notice-form" class="sc-notice-form" ${UIEvents.attr("submit", function(event,captured){SettingsView.saveUpdateNotice(event);}, [])}>
                    <div class="sc-grid">
                        <div class="form-group">
                            <label class="form-label" for="sc-notice-at">Fecha y hora de la actualización *</label>
                            <input id="sc-notice-at" name="scheduledAt" type="datetime-local" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="sc-notice-note">Nota (opcional)</label>
                            <input id="sc-notice-note" name="note" type="text" class="form-input" maxlength="300" placeholder="Por ejemplo, qué cambia">
                        </div>
                    </div>
                    <div class="sc-notice-actions">
                        <button type="button" class="btn btn-secondary" id="sc-notice-remove" hidden ${UIEvents.attr("click", function(event,captured){SettingsView.removeUpdateNotice();}, [])}><i class="fas fa-bell-slash"></i> Quitar aviso</button>
                        <button type="submit" class="btn btn-primary" id="sc-notice-save"><i class="fas fa-bell"></i> Activar aviso</button>
                    </div>
                </form>
            </section>`;
    },

    /** "2026-10-19T00:30" in the browser's time zone, for a datetime-local field. */
    localInputValue(date) {
        const pad = n => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    showUpdateNotice(notice) {
        const status = document.getElementById('sc-notice-status');
        const input = document.getElementById('sc-notice-at');
        const note = document.getElementById('sc-notice-note');
        const remove = document.getElementById('sc-notice-remove');
        const save = document.getElementById('sc-notice-save');
        if (!status || !input) return;
        input.min = this.localInputValue(new Date());
        if (notice) {
            const at = new Date(notice.scheduledAt);
            status.textContent = `Aviso activo: actualización el ${UpdateNotice.formatDate(at)} a las ${UpdateNotice.formatTime(at)}.${notice.note ? ` Nota: «${notice.note}».` : ''}`;
            status.classList.add('is-active');
            input.value = this.localInputValue(at);
            note.value = notice.note || '';
        } else {
            status.textContent = 'No hay ningún aviso programado.';
            status.classList.remove('is-active');
        }
        remove.hidden = !notice;
        SafeHTML.set(save, `<i class="fas fa-bell"></i> ${notice ? 'Cambiar aviso' : 'Activar aviso'}`);
    },

    async loadUpdateNotice() {
        if (!AuthManager.isAdmin()) return;
        try {
            const response = await fetch('/api/update-notice', { cache: 'no-store' });
            const body = await response.json();
            if (!response.ok || !body.success) throw new Error();
            this.showUpdateNotice(body.notice);
        } catch {
            const status = document.getElementById('sc-notice-status');
            if (status) status.textContent = 'No se pudo cargar el aviso. Recarga la página para reintentarlo.';
        }
    },

    async saveUpdateNotice(event) {
        event.preventDefault();
        const form = event.target, button = document.getElementById('sc-notice-save');
        if (!AuthManager.isAdmin() || button.disabled) return;
        const at = new Date(form.scheduledAt.value);
        if (Number.isNaN(at.getTime()) || at <= new Date()) {
            Utils.showToast('Elige una fecha y hora futuras.', 'error');
            return;
        }
        button.disabled = true;
        try {
            const response = await fetch('/api/update-notice', { method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ scheduledAt: at.toISOString(), note: form.note.value }) });
            const body = await response.json();
            if (!response.ok || !body.success) throw new Error(body.error);
            this.showUpdateNotice(body.notice);
            Utils.showToast('Aviso activado: cada persona lo verá una vez.', 'success');
        } catch (e) {
            Utils.showToast(e.message || 'No se pudo guardar el aviso.', 'error');
        } finally {
            button.disabled = false;
        }
    },

    removeUpdateNotice() {
        Utils.showConfirm('¿Quitar el aviso de actualización? Quien aún no lo haya visto ya no lo verá.', async () => {
            try {
                const response = await fetch('/api/update-notice', { method: 'DELETE' });
                if (!response.ok) throw new Error();
                document.getElementById('sc-notice-form')?.reset();
                this.showUpdateNotice(null);
                Utils.showToast('Aviso quitado.', 'info');
            } catch {
                Utils.showToast('No se pudo quitar el aviso.', 'error');
            }
        });
    },

    // --- Datos del centro: the school's identity, used on login, enrollment, prints and emails ---
    SCHOOL_FIELDS: [
        {key: 'name', label: 'Nombre del centro *', placeholder: 'Nombre completo', max: 120, required: true},
        {key: 'shortName', label: 'Siglas o nombre corto', placeholder: 'Se usa en menús y asuntos de correo', max: 30},
        {key: 'legalName', label: 'Razón social', placeholder: 'Titular legal del centro', max: 160},
        {key: 'website', label: 'Web', placeholder: 'https://…', max: 300, type: 'url'},
        {key: 'contactUrl', label: 'Página de contacto', placeholder: 'https://… (enlace «¿Tienes dudas?» de la inscripción)', max: 300, type: 'url'},
        {key: 'contactEmail', label: 'Correo de contacto', placeholder: 'Para alumnado y solicitantes', max: 254, type: 'email'},
        {key: 'supportEmail', label: 'Correo de soporte', placeholder: 'Para incidencias de la plataforma', max: 254, type: 'email'},
        {key: 'phone', label: 'Teléfono', max: 40, type: 'tel'},
        {key: 'address', label: 'Dirección', max: 200},
        {key: 'mapUrl', label: 'Enlace al mapa', placeholder: 'https://…', max: 1000, type: 'url'},
        {key: 'logoUrl', label: 'Logo principal (barra lateral, impresos y fichas)', placeholder: 'Sube una imagen o pega https://…', max: 500},
        {key: 'logoLightUrl', label: 'Logo claro (pantalla de entrada e Inscríbete)', placeholder: 'Sube una imagen o pega https://…', max: 500},
        {key: 'emailLogoUrl', label: 'Logo para correos', placeholder: 'Sube una imagen o pega https://…', max: 500},
        {key: 'iconUrl', label: 'Icono (pestaña del navegador y móvil)', placeholder: 'Imagen cuadrada, mín. 180 × 180', max: 500}
    ],

    SCHOOL_TABS: {
        centro: ['name', 'shortName', 'legalName', 'website'],
        contacto: ['contactUrl', 'contactEmail', 'supportEmail', 'phone', 'address', 'mapUrl'],
        imagen: ['logoUrl', 'logoLightUrl', 'emailLogoUrl', 'iconUrl']
    },

    renderSchoolProfile(tab) {
        const editable = AuthManager.isAdmin();
        const value = key => Utils.sanitizeHTML(SchoolService.get(key));
        const field = key => {
            const f = this.SCHOOL_FIELDS.find(item => item.key === key);
            return `
                <div class="form-group">
                    <label class="form-label" for="sc-${f.key}">${f.label}</label>
                    <input id="sc-${f.key}" name="${f.key}" type="${f.type || 'text'}" class="form-input" maxlength="${f.max}" ${f.required ? 'required' : ''} value="${value(f.key)}" placeholder="${Utils.sanitizeHTML(f.placeholder || '')}"
                        ${UIEvents.attr("input", function(event,captured){SettingsView.updatePreview(this.form);}, [])}>
                </div>`;
        };
        const panel = (id, title, help, body) => `
            <section class="card sc-panel" role="tabpanel" id="sc-panel-${id}" aria-labelledby="sc-tab-${id}" ${tab === id ? '' : 'hidden'}>
                ${title ? `<h2 class="sc-panel-title">${title}</h2>` : ''}
                ${help ? `<p class="sc-help">${help}</p>` : ''}
                ${body}
            </section>`;
        const safeImage = url => /^https:\/\//.test(url) || /^\/school-assets\/[0-9a-f-]{36}\.(png|jpg|webp)$/.test(url) ? url : '';
        const lightLogo = safeImage(SchoolService.get('logoLightUrl'));
        return `
            <form id="school-profile-form" class="sc-form" ${UIEvents.attr("submit", function(event,captured){SettingsView.saveSchoolProfile(event);}, [])}
                ${UIEvents.attr("input", function(event,captured){SettingsView.trackChanges(this);}, [])}>
                <fieldset ${editable ? '' : 'disabled'} class="sc-fields">
                    <section class="sc-panel sc-centro" role="tabpanel" id="sc-panel-centro" aria-labelledby="sc-tab-centro" ${tab === 'centro' ? '' : 'hidden'}>
                        <div class="sc-centro-card">
                        <div class="dnr-hero sc-hero">
                            <div class="sc-hero-main">
                                <div class="sc-hero-logo"><img id="sc-preview-hero-logo" src="${Utils.sanitizeHTML(lightLogo)}" alt="" ${lightLogo ? '' : 'hidden'}></div>
                                <div>
                                    <div class="dnr-hero-eyebrow">Ficha del centro</div>
                                    <h1 id="sc-preview-name">${value('name') || 'Nombre del centro'}</h1>
                                    <p id="sc-preview-legal">${value('legalName') || 'Razón social sin indicar'}</p>
                                    <div class="sc-hero-chips">
                                        <span class="sc-chip"><i class="fas fa-tag"></i> <span id="sc-preview-short">${value('shortName') || '—'}</span></span>
                                        <span class="sc-chip"><i class="fas fa-globe"></i> <span id="sc-preview-web">${value('website') || 'Sin web'}</span></span>
                                        ${SchoolService.get('phone') ? `<span class="sc-chip"><i class="fas fa-phone"></i> ${value('phone')}</span>` : ''}
                                    </div>
                                </div>
                            </div>
                            <div class="dnr-hero-stat sc-hero-stat">
                                <div class="dnr-num">${Utils.sanitizeHTML(SchoolService.shortName())}</div>
                                <div class="dnr-label">Así aparece en menús y correos</div>
                            </div>
                        </div>
                        <div class="dnr-sprocket sc-sprocket" aria-hidden="true"></div>
                        </div>
                        <div class="card sc-card-body">
                            <p class="sc-help">Aparecen en el acceso, el formulario de inscripción, los impresos y los correos (variables {{escuela}}, {{escuelaSiglas}}…).${editable ? '' : ' Solo administración puede cambiarlos.'}</p>
                            <div class="sc-grid">${this.SCHOOL_TABS.centro.map(field).join('')}</div>
                        </div>
                    </section>
                    ${panel('contacto', 'Contacto', 'Cómo os encuentran el alumnado y quienes se inscriben. El correo de soporte sale en «Ayuda y soporte».',
                        `<div class="sc-grid">${this.SCHOOL_TABS.contacto.map(field).join('')}</div>`)}
                    ${panel('imagen', 'Imagen', 'Sube una imagen nueva para sustituir la actual, o pega la dirección (https) de una imagen pública. Las de otra web deben estar permitidas en el servidor (SCHOOL_IMAGE_ORIGINS) para verse.', `
                        <div class="sc-logos">
                            ${[['logoUrl', 'is-light', 'Vista previa del logo principal'], ['logoLightUrl', 'is-dark', 'Vista previa del logo claro'], ['emailLogoUrl', 'is-light', 'Vista previa del logo para correos'], ['iconUrl', 'is-light is-icon', 'Vista previa del icono']].map(([key, tone, alt]) => `
                            <div class="sc-logo-card">
                                ${field(key)}
                                <div class="sc-logo-preview ${tone}"><img id="sc-preview-${key}" src="${Utils.sanitizeHTML(safeImage(SchoolService.get(key)))}" alt="${alt}" ${safeImage(SchoolService.get(key)) ? '' : 'hidden'}><span class="sc-logo-empty" id="sc-empty-${key}" ${safeImage(SchoolService.get(key)) ? 'hidden' : ''}>Sin imagen: se muestra el nombre del centro</span></div>
                                ${editable ? `<div class="sc-logo-actions">
                                    <label class="btn btn-secondary btn-sm sc-upload">
                                        <i class="fas fa-upload"></i> Subir imagen
                                        <input type="file" accept="image/png,image/jpeg,image/webp" class="sc-upload-input" ${UIEvents.attr("change", function(event,captured){SettingsView.uploadLogo(String(captured[0]),this);}, [key])}>
                                    </label>
                                    <span class="sc-upload-hint">PNG, JPEG o WebP · máx. 2 MB · se guarda al subirla</span>
                                </div>` : ''}
                            </div>`).join('')}
                        </div>`)}
                    ${panel('privacidad', 'Privacidad', 'Texto de «política de privacidad» del formulario de inscripción. Un párrafo por línea en blanco; «Responsable: …» al principio de un párrafo sale en negrita. Si subes un PDF, se podrá descargar desde la misma ventana.', `
                        <div class="form-group">
                            <label class="form-label" for="sc-privacyText">Política de privacidad</label>
                            <textarea id="sc-privacyText" name="privacyText" class="form-input sc-privacy" rows="14" maxlength="8000">${value('privacyText')}</textarea>
                        </div>
                        ${this.renderDocumentBlock('privacyPdfUrl', 'PDF de la política de privacidad', editable)}`)}
                    ${panel('normativa', 'Normativa', 'Normativa de la escuela que el solicitante acepta en el formulario de inscripción. Mismo formato que la privacidad: un párrafo por línea en blanco; «Título: …» al principio sale en negrita.', `
                        <div class="form-group">
                            <label class="form-label" for="sc-rulesText">Normativa de la escuela</label>
                            <textarea id="sc-rulesText" name="rulesText" class="form-input sc-privacy" rows="16" maxlength="20000">${value('rulesText')}</textarea>
                        </div>
                        ${this.renderDocumentBlock('rulesPdfUrl', 'PDF de la normativa', editable)}`)}
                </fieldset>
                ${editable ? `<div class="sc-save-bar sca-save" id="sc-save-bar" ${this.OWN_SAVE_TABS.includes(tab) ? 'hidden' : ''}>
                    <span id="school-profile-status" role="status" class="sca-dirty">Sin cambios</span>
                    <button type="button" class="sca-btn" id="sca-discard" disabled ${UIEvents.attr("click", function(event,captured){SettingsView.discardChanges();}, [])}>Descartar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save" aria-hidden="true"></i> Guardar cambios</button>
                </div>` : ''}
            </form>`;
    },

    // PDF of the privacy policy or the school rules: uploaded (saved at once) or removed; the
    // hidden field keeps the main «Guardar» in step with it.
    renderDocumentBlock(slot, title, editable) {
        const url = SchoolService.get(slot);
        const has = /^\/school-assets\/[0-9a-f-]{36}\.pdf$/.test(url);
        return `
            <div class="sc-doc" id="sc-doc-${slot}">
                <input type="hidden" name="${slot}" value="${Utils.sanitizeHTML(has ? url : '')}">
                <div class="sc-doc-icon"><i class="fas fa-file-pdf"></i></div>
                <div class="sc-doc-info">
                    <strong>${title}</strong>
                    <span class="sc-doc-state">${has ? 'Subido. En la inscripción aparece «Descargar PDF».' : 'Sin PDF: solo se muestra el texto.'}</span>
                </div>
                <div class="sc-doc-actions">
                    ${has ? `<a class="btn btn-secondary btn-sm" href="${Utils.sanitizeHTML(url)}" download><i class="fas fa-download"></i> Descargar</a>` : ''}
                    ${editable ? `<label class="btn btn-secondary btn-sm sc-upload">
                        <i class="fas fa-upload"></i> ${has ? 'Sustituir PDF' : 'Subir PDF'}
                        <input type="file" accept="application/pdf" class="sc-upload-input" ${UIEvents.attr("change", function(event,captured){SettingsView.uploadDocument(String(captured[0]),this);}, [slot])}>
                    </label>
                    ${has ? `<button type="button" class="btn btn-secondary btn-sm sc-doc-remove" ${UIEvents.attr("click", function(event,captured){SettingsView.removeDocument(String(captured[0]));}, [slot])}><i class="fas fa-trash"></i> Quitar</button>` : ''}` : ''}
                </div>
            </div>`;
    },

    refreshDocumentBlock(slot) {
        const block = document.getElementById(`sc-doc-${slot}`);
        const title = block?.querySelector('strong')?.textContent || '';
        if (block) SafeHTML.replace(block, this.renderDocumentBlock(slot, title, AuthManager.isAdmin()));
    },

    async uploadDocument(slot, input) {
        const file = input.files?.[0];
        input.value = '';
        if (!file || !AuthManager.isAdmin() || this._uploadingLogo) return;
        if (file.type !== 'application/pdf' || file.size > 10 * 1024 * 1024) { Utils.showToast('Sube un PDF de hasta 10 MB.', 'error'); return; }
        this._uploadingLogo = true;
        input.closest('label')?.classList.add('is-busy');
        try {
            await SchoolService.uploadDocument(slot, file);
            this.refreshDocumentBlock(slot);
            Utils.showToast('PDF guardado.', 'success');
        } catch (error) {
            Utils.showToast(error.message, 'error');
            input.closest('label')?.classList.remove('is-busy');
        } finally {
            this._uploadingLogo = false;
        }
    },

    async removeDocument(slot) {
        if (!AuthManager.isAdmin()) return;
        const answer = await SafeHTML.fire({icon: 'warning', title: '¿Quitar el PDF?', text: 'En la inscripción solo se mostrará el texto.', showCancelButton: true, confirmButtonText: 'Quitar', cancelButtonText: 'Cancelar'});
        if (!answer?.isConfirmed) return;
        try {
            await SchoolService.save({...SchoolService.profile, [slot]: ''});
            this.refreshDocumentBlock(slot);
            Utils.showToast('PDF quitado.', 'success');
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    async uploadLogo(slot, input) {
        const file = input.files?.[0];
        input.value = '';
        if (!file || !AuthManager.isAdmin() || this._uploadingLogo) return;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
            Utils.showToast('Sube una imagen PNG, JPEG o WebP de hasta 2 MB.', 'error');
            return;
        }
        this._uploadingLogo = true;
        const label = input.closest('label');
        label?.classList.add('is-busy');
        try {
            const profile = await SchoolService.uploadLogo(slot, file);
            const url = profile[slot];
            const field = document.getElementById(`sc-${slot}`);
            if (field) { field.value = url; field.defaultValue = url; this.trackChanges(field.form); }
            const preview = document.getElementById(`sc-preview-${slot}`);
            if (preview) { preview.src = url; preview.hidden = false; }
            const empty = document.getElementById(`sc-empty-${slot}`);
            if (empty) empty.hidden = true;
            const hero = document.getElementById('sc-preview-hero-logo');
            if (hero && slot === 'logoLightUrl') { hero.src = url; hero.hidden = false; }
            App.applySchoolBranding?.();
            Utils.showToast('Logo actualizado.', 'success');
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            this._uploadingLogo = false;
            label?.classList.remove('is-busy');
        }
    },

    // Live preview of the header card and logos while typing (nothing is saved until «Guardar»).
    updatePreview(form) {
        const set = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };
        set('sc-preview-name', form.elements.name.value.trim() || 'Nombre del centro');
        set('sc-preview-legal', form.elements.legalName.value.trim() || 'Razón social sin indicar');
        set('sc-preview-short', form.elements.shortName.value.trim() || '—');
        set('sc-preview-web', form.elements.website.value.trim() || 'Sin web');
        for (const key of this.SCHOOL_TABS.imagen) {
            const url = form.elements[key].value.trim();
            const img = document.getElementById(`sc-preview-${key}`), empty = document.getElementById(`sc-empty-${key}`);
            const valid = /^https:\/\/[^\s"'<>]+$/.test(url) || /^\/school-assets\/[0-9a-f-]{36}\.(png|jpg|webp)$/.test(url);
            if (img && valid) img.src = url;
            if (img) img.hidden = !valid;
            if (empty) empty.hidden = valid;
        }
    },

    async saveSchoolProfile(event) {
        event.preventDefault();
        if (!AuthManager.isAdmin() || this._savingSchool) return;
        const form = event.target;
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const data = Object.fromEntries([...this.SCHOOL_FIELDS.map(f => f.key), 'privacyText', 'rulesText', 'privacyPdfUrl', 'rulesPdfUrl'].map(key => [key, form.elements[key].value]));
        const status = document.getElementById('school-profile-status');
        const button = form.querySelector('button[type="submit"]');
        this._savingSchool = true; button.disabled = true;
        if (status) status.textContent = 'Guardando…';
        try {
            await SchoolService.save(data);
            if (typeof App !== 'undefined' && App.applySchoolBranding) App.applySchoolBranding();
            Utils.showToast('Datos del centro guardados.', 'success');
            // Redraw with the saved values (the open tab is kept).
            if (typeof App !== 'undefined') App.render();
        } catch (error) {
            // The form keeps what was typed so nothing is lost.
            if (status) status.textContent = '';
            Utils.showToast(error.message, 'error');
        } finally {
            this._savingSchool = false; button.disabled = false;
        }
    },

    // «N cambios sin guardar»: fields whose value differs from the saved one are marked.
    trackChanges(form) {
        if (!form || !form.elements) return;
        let n = 0;
        [...form.elements].forEach(el => {
            if (!el.name || el.type === 'hidden' || el.type === 'file' || !('defaultValue' in el)) return;
            const changed = el.value !== el.defaultValue;
            el.classList.toggle('is-changed', changed);
            if (changed) n++;
        });
        const status = document.getElementById('school-profile-status');
        if (status && !this._savingSchool) status.textContent = n ? `${n} ${n === 1 ? 'cambio' : 'cambios'} sin guardar` : 'Sin cambios';
        document.getElementById('sc-save-bar')?.classList.toggle('is-dirty', n > 0);
        const discard = document.getElementById('sca-discard');
        if (discard) discard.disabled = !n;
        this.updateMarks(form);
    },

    discardChanges() {
        const form = document.getElementById('school-profile-form');
        if (!form) return;
        form.reset();
        this.updatePreview(form);
        this.trackChanges(form);
    },

    // What each section of the menu still lacks (from the form as it is now).
    updateMarks(form = document.getElementById('school-profile-form')) {
        const val = key => (form?.elements?.[key]?.value ?? SchoolService.get(key) ?? '').trim();
        const set = (id, text, warn = false) => { const el = document.getElementById(`sca-mark-${id}`); if (el) { el.textContent = text; el.classList.toggle('is-warn', warn); } };
        set('centro', val('name') ? '✓' : 'falta', !val('name'));
        const contact = ['contactEmail', 'phone', 'address'].filter(k => !val(k)).length;
        set('contacto', contact ? `falta ${contact}` : '✓', !!contact);
        const logos = this.SCHOOL_TABS.imagen.filter(k => !val(k)).length;
        set('imagen', logos ? `falta ${logos}` : '✓', !!logos);
        set('privacidad', val('privacyText') ? '✓' : 'vacío', !val('privacyText'));
        set('normativa', val('rulesText') ? '✓' : 'vacío', !val('rulesText'));
        const from = `${this.currentYear}-09-01`, to = `${this.currentYear + 1}-08-31`;
        // Weekends are days off anyway: count the festivos on school days.
        const course = (this.getGlobalSettings().holidays || []).filter(d => d >= from && d <= to);
        const count = course.filter(d => ![0, 6].includes(new Date(`${d}T12:00:00`).getDay())).length;
        set('calendario', String(count));
        const pill = document.getElementById('sca-holiday-count');
        if (pill) {
            pill.textContent = `${count} ${count === 1 ? 'festivo' : 'festivos'} entre semana`;
            pill.title = course.length > count ? `Además hay ${course.length - count} sábados o domingos marcados como festivo.` : '';
        }
    },

    getGlobalSettings() {
        const stored = DataManager.getAll(DataManager.KEYS.GLOBAL_SETTINGS);
        // getAll returns array, but we want a single object for settings. 
        // If it's an array for some reason (legacy), pick first or empty.
        // Actually DataManager generic is for arrays mostly. Let's treat it as a singleton array or handle properly.
        // DataManager.saveAll stringifies the data.
        // Let's assume we store an array of 1 object or modify DataManager? 
        // For consistency with current DataManager, let's treat it as an array where index 0 is our settings object.
        if (Array.isArray(stored) && stored.length > 0) {
            return stored[0];
        }
        return { holidays: [] };
    },

    cacheHolidays(holidays) {
        const settings = [{holidays}];
        FileStorage.dataStore.settings = settings;
        BrowserState.setItem(DataManager.KEYS.GLOBAL_SETTINGS, JSON.stringify(settings));
    },

    validHolidays(value) {
        return Array.isArray(value) && value.every(date => typeof date === 'string' &&
            /^20\d{2}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date)) &&
            new Date(date).toISOString().slice(0,10) === date);
    },

    updateControls(message = '') {
        const grid = document.querySelector('.settings-container .calendar-year-grid');
        if (!grid) return;
        const disabled = !this.canManage() || !this._ready || this._saving;
        grid.setAttribute('aria-busy', String(disabled));
        grid.querySelectorAll('[data-holiday-date]').forEach(button => {
            button.disabled = disabled;
            const active = this.getGlobalSettings().holidays.includes(button.dataset.holidayDate);
            button.classList.toggle('is-holiday', active);
            const weekend = [0,6].includes(new Date(button.dataset.holidayDate+'T12:00:00').getDay());
            button.classList.toggle('is-weekend', !active && weekend);
            button.classList.toggle('is-weekend-day', weekend);
            button.setAttribute('aria-pressed', String(active));
            button.title = active ? 'Desmarcar festivo' : 'Marcar como festivo';
        });
        this.updateMarks();
        const status = document.getElementById('holidays-status');
        if (status) status.textContent = message; // Saving is silent: a line appearing for a moment shifted the grid.
    },

    async init() {
        if (!this.canManage()) return;
        this.loadUpdateNotice();
        const request = ++this._request, epoch = AuthManager._epoch;
        this._ready = false;
        this.updateControls('Cargando calendario…');
        try {
            const response = await fetch('/data/settings.json', {cache:'no-store'});
            const data = await response.json();
            if (request !== this._request || epoch !== AuthManager._epoch) return;
            const holidays = Array.isArray(data) && data.length === 0 ? [] : data?.[0]?.holidays;
            if (!response.ok || !this.validHolidays(holidays)) throw new Error('Calendario no disponible');
            this.cacheHolidays(holidays);
            this._ready = true;
            this.updateControls();
        } catch {
            if (request === this._request && epoch === AuthManager._epoch) this.updateControls('No se pudo cargar el calendario. Recarga la página para reintentarlo.');
        }
    },

    destroy() { ++this._request; this._ready = false; },

    async toggleHoliday(dateString) {
        if (!this.canManage() || !this._ready || this._saving || !this.validHolidays([dateString])) return;
        const epoch = AuthManager._epoch, request = this._request;
        const current = () => epoch === AuthManager._epoch && request === this._request;
        const holiday = !this.getGlobalSettings().holidays.includes(dateString);
        this._saving = true;
        this.updateControls();
        try {
            const response = await fetch('/api/settings/holidays', {
                method:'PUT',
                headers:{'Content-Type':'application/json','X-SICAF-CSRF':AuthManager.getCsrfToken()},
                body:JSON.stringify({date:dateString, holiday})
            });
            const result = await response.json().catch(() => null);
            if (!current()) return;
            if (!response.ok || !result?.success || !this.validHolidays(result.holidays) ||
                result.holidays.includes(dateString) !== holiday) throw new Error('No se pudo confirmar el guardado del festivo. Recarga el calendario antes de reintentarlo.');
            this.cacheHolidays(result.holidays);
        } catch (error) {
            if (current()) {
                this._ready = false; // A lost response may have committed; reload before toggling again.
                Utils.showToast(error.message || 'No se pudo guardar el festivo.', 'error');
            }
        } finally {
            this._saving = false;
            if (current()) this.updateControls(this._ready ? '' : 'No se pudo confirmar el guardado. Recarga el calendario antes de reintentarlo.');
            else if (epoch === AuthManager._epoch && document.querySelector('.settings-container .calendar-year-grid')) await this.init();
        }
    },

    changeYear(delta) {
        if (this._saving) return;
        this.currentYear += delta;
        App.render();
    },

    renderMonths(startYear, holidays) {
        const monthsHTML = [];
        const monthNames = [
            'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto'
        ];

        // Academic year: Sept (8) of startYear to Aug (7) of startYear + 1
        for (let i = 0; i < 12; i++) {
            const monthIndex = (i + 8) % 12; // 0=Jan, 8=Sept
            // Correct logic check:
            // if monthIndex >= 8 (Sept-Dec), year is startYear.
            // if monthIndex < 8 (Jan-Aug), year is startYear + 1.
            const realYear = monthIndex >= 8 ? startYear : startYear + 1;

            monthsHTML.push(this.renderMonth(realYear, monthIndex, holidays, monthNames[i]));
        }

        return monthsHTML.join('');
    },

    renderMonth(year, month, holidays, monthLabel) {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Mon=0, Sun=6 adjustment

        let daysHTML = '';

        // Empty cells for days before the 1st
        for (let i = 0; i < startDayOfWeek; i++) {
            daysHTML += `<div></div>`;
        }

        // Days
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isHoliday = holidays.includes(dateStr);
            const date = new Date(year, month, day);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            let classes = 'day-cell';
            if (isHoliday) classes += ' is-holiday';
            else if (isWeekend) classes += ' is-weekend';
            // A weekend marked as festivo is drawn softer: the strong red is for school days off.
            if (isWeekend) classes += ' is-weekend-day';

            daysHTML += `
                <button type="button" class="${classes}" data-holiday-date="${dateStr}"
                     ${!this._ready || this._saving || !this.canManage() ? 'disabled' : ''}
                     aria-pressed="${isHoliday}" aria-label="${dateStr}"
                     ${UIEvents.attr("click", function(event,captured){SettingsView.toggleHoliday(String(captured[0]));}, [dateStr])}
                     title="${isHoliday ? 'Desmarcar festivo' : 'Marcar como festivo'}">
                    ${day}
                </button>
            `;
        }

        return `
            <div class="month-card">
                <div class="month-header">
                    <h3>${monthLabel}</h3>
                </div>
                <div class="month-days-grid">
                    <div class="day-header">L</div>
                    <div class="day-header">M</div>
                    <div class="day-header">X</div>
                    <div class="day-header">J</div>
                    <div class="day-header">V</div>
                    <div class="day-header">S</div>
                    <div class="day-header">D</div>
                    ${daysHTML}
                </div>
            </div>
        `;
    },


};

window.SettingsView = SettingsView;
